import { html, css } from 'lit';

import '../../../packages/neb-lit-components/src/components/inputs/neb-select-search';
import { PAYER_PLAN_VERSION_CLAIMS } from '../../../packages/neb-api-client/src/payer-plan-api-client';
import { PayerPlanQueryService } from '../../../packages/neb-api-client/src/services/payer-plan';
import { BUTTON_ROLE } from '../../../packages/neb-lit-components/src/components/neb-button';
import NebPopup from '../../../packages/neb-popup/src/neb-popup';
import Debouncer from '../../../packages/neb-utils/debouncer';
import { CSS_FONT_FAMILY, CSS_SPACING } from '../../styles';

export const ELEMENTS = {
  payerDropdown: {
    id: 'payer-dropdown',
  },
  buttonReplace: {
    id: 'button-replace',
  },
  buttonCancel: {
    id: 'button-cancel',
  },
  message: {
    id: 'popup-message',
  },
};

class NebPopupReplaceInsurancePayer extends NebPopup {
  static get properties() {
    return {
      __payerPlans: {
        type: Array,
      },
      __payerSearchValue: {
        type: String,
      },
      __selectedPayerPlanId: {
        type: String,
      },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          width: 525px;
          font-family: ${CSS_FONT_FAMILY};
        }

        :host([layout='small']) {
          width: 350px;
        }

        .content-container {
          display: flex;
          flex-direction: column;
          flex: 1 0 0;
          justify-content: space-between;
          height: 100%;
        }

        .button-container {
          display: flex;
          justify-content: start;
          margin-top: 30px;
        }

        .spacing {
          margin-right: 10px;
        }

        .padding-top {
          padding-top: ${CSS_SPACING};
        }

        .italic-text {
          font-style: italic;
        }
      `,
    ];
  }

  initState() {
    super.initState();

    this.title = 'Replace Payer';
    this.model = {
      payerPlan: {},
      planInfo: {},
    };

    this.__payerPlans = [];
    this.__payerSearchValue = '';
    this.__selectedPayerPlanId = '';
    this.__query = {
      hideInactive: true,
      search: '',
    };

    this.__searchDebouncer = new Debouncer(() => {
      this.__payerPlanQueryService.update(this.__query);
    });
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      confirm: () => {
        if (!this.__selectedPayerPlanId) return;

        this.onClose({
          payerPlanId: this.__selectedPayerPlanId,
        });
      },
      payerSelected: ({ value }) => {
        this.__selectedPayerPlanId = value && value !== '' ? value.id : '';
      },
      search: ({ value: search }) => {
        if (search !== this.__payerSearchValue) {
          this.__payerSearchValue = search;
          this.__query.search = search;

          this.__searchDebouncer.debounce();
        }
      },
    };
  }

  __setupPayerPlanQueryService() {
    this.__payerPlanQueryService = new PayerPlanQueryService(
      ({ model: payerPlans }) => {
        this.__payerPlans = payerPlans
          .filter(payer => payer.id !== this.model.payerPlan.id)
          .map(payer => ({
            ...payer,
            label: `(${payer.alias}) ${
              payer.payerName ? `${payer.payerName}` : ''
            }`,
          }));
      },
      PAYER_PLAN_VERSION_CLAIMS,
    );
  }

  __getSelectedPayer() {
    return this.__payerPlans.find(
      payer => payer.id === this.__selectedPayerPlanId,
    );
  }

  __getPopupMessage() {
    return html`
      <div id="${ELEMENTS.message.id}">
        Replacing the payer will copy details from the plan
        <b class="italic-text">${this.model.planInfo.planName}</b> and create a
        new one with the updated payer. The original plan will be inactivated.
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.__setupPayerPlanQueryService();

    this.__payerPlanQueryService.update(this.__query);
  }

  renderContent() {
    return html`
      <div class="content-container">
        ${this.__getPopupMessage()}
        <neb-select-search
          id="${ELEMENTS.payerDropdown.id}"
          name="payerPlanId"
          class="search"
          label="Payer"
          .items="${this.__payerPlans}"
          .search="${this.__payerSearchValue}"
          .value="${this.__getSelectedPayer()}"
          .onChange="${this.handlers.payerSelected}"
          .onSearch="${this.handlers.search}"
          showSearch
        ></neb-select-search>

        <div class="button-container">
          <neb-button
            id="${ELEMENTS.buttonReplace.id}"
            class="spacing"
            label="Replace"
            .role="${BUTTON_ROLE.CONFIRM}"
            .onClick="${this.handlers.confirm}"
            ?disabled="${!this.__selectedPayerPlanId}"
          ></neb-button>

          <neb-button
            id="${ELEMENTS.buttonCancel.id}"
            label="Cancel"
            .role="${BUTTON_ROLE.OUTLINE}"
            .onClick="${this.handlers.cancel}"
          ></neb-button>
        </div>
      </div>
    `;
  }
}

customElements.define(
  'neb-popup-replace-insurance-payer',
  NebPopupReplaceInsurancePayer,
);
